import { createApp } from "vue";
import axios from "axios";
import { createI18n } from "vue-i18n";
import BootstrapVue, {
  AlertPlugin,
  BadgePlugin,
  ListGroupPlugin,
  ModalPlugin,
  SpinnerPlugin,
  TooltipPlugin,
} from "bootstrap-vue";
import { VueReCaptcha } from "vue-recaptcha-v3";
import vuescroll from "vuescroll";
import VueMask from "v-mask";
import VueQr from "vue-qr";
import resizeSensor from "vue-resize-sensor";
// import DatePicker from "vue2-datepicker";
import VueClipboard from "vue-clipboard2";
import DecimalNumber from "decimal.js";
import * as CryptoJS from "crypto-js";
import * as crypto from "crypto";
import { mapGetters } from "vuex";
import router from "@/router";
import store from "@/store";
import {
  REFRESH_TOKEN,
  AUTO_SIGN_IN,
  SSO_SIGN_IN,
  ALCHEMY_DOMAIN,
} from "@/store/modules/auth/consts";
import {
  IFRAME_STATUS,
  IFRAME_STATUS_OPEN,
  IFRAME_STATUS_CLOSE,
  GET_USER_INFO,
  SET_HOSTED_PAGE_INFO,
} from "@/store/modules/user/consts";
import {
  RESET_IFRAME_CART_INIT,
  RESET_IFRAME_CLOSE_MODAL,
} from "@/utils/event.constants";
import { teardownBootstrapModals } from "@/utils/modalCleanup";
import {
  releasePaymentResult,
  resetPaymentResultEvents,
  sendPaymentResult,
} from "@/utils/paymentResultEvents";
import App from "./App.vue";
import Helper from "./utils/helper";
import { sessionTimeoutNotificationType } from "@/store/modules/user/enums";
import { languages, defaultLocale } from "@/i18n/index.js";
import DefaultLayout from "@/layouts/default";
import Loader from "@/components/Loader";
import SavePaymentMethodModal from "@/components/SavePaymentMethodModal";
import PoweredBy from "@/components/PoweredBy";
import { Form, Field, ErrorMessage } from "vee-validate";
import BackBtn from "@/components/BackBtn";
import Sardine from "@/components/Sardine";
import helper from "./utils/helper";
import { persistCartEmail } from "./utils/cartEmail";
import {
  applyOrderKycFields,
  mergeIframeCartPayload,
  prepareIframeCartFromPostMessage,
} from "./utils/iframeCart";
import {
  loadMerchantMarketInfo,
  prefetchPaymentBootstrap,
} from "./utils/paymentBootstrap";
import { getCartSessionKey } from "./utils/sessionDataCache";
import {
  beginIframeCartLoading,
  endIframeCartLoading,
  isIframeCartLoadingActive,
} from "./utils/routeLoader";
import TextInput from "@/components/TextInput.vue";
import {
  BANKS_ARR,
  BANK,
  BANK_TRANSFER,
  WALLET_TRANSFER,
  CRYPTO,
} from "./utils/stringconsts";
import { routeEnum } from "./store/modules/auth/enum";
import { Buffer } from "buffer";
import mitt from "mitt";
import CryptoWalletVue from "./components/CryptoWallet.vue";
import WalletConnectVue from "./components/walletconnect/WalletConnect.vue";
// inititalize buffer at global level
import { Buffer as BufferPolyfill } from "buffer";
import process from "process";
import VerticalProgressBarVue from "./components/VerticalProgressBar.vue";
import BoxLoaderVue from "./components/BoxLoader.vue";
import BackCustomBtn from "./components/BackCustomBtn.vue";
import CustomCollapse from "./components/CustomCollapse.vue";
import partialPaymentsVue from "./views/partial-payments.vue";
import * as Sentry from "@sentry/vue";
import config from "./utils/config";
import { fetchAndLogVersionJson } from "@/utils/versionJsonLog";
import {
  applyIntegrationFromCart,
  applyIntegrationFromParentMessage,
  logActivityIntegrationStart,
  resolveCartMerchantId,
} from "@/utils/activityLogContext";
import { initializeBrowserOtel } from "@/utils/obs";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { wagmiAdapter } from "./utils/wagmi.config";
import { WagmiPlugin } from "@wagmi/vue";
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5000 } },
});
globalThis.Buffer = BufferPolyfill;
globalThis.process = process;
// event emitter intiatlized
const emitter = mitt();
// app created
const app = createApp(App);

function storageFactory(e) {
  let t = {};
  function n() {
    try {
      const t = "RKFL";
      return e().setItem(t, t), e().removeItem(t), !0;
    } catch (e) {
      return !1;
    }
  }
  return {
    getItem(r) {
      return n() ? e().getItem(r) : t.hasOwnProperty(r) ? t[r] : null;
    },
    setItem(r, o) {
      n() ? e().setItem(r, o) : (t[r] = String(o));
    },
    removeItem(r) {
      n() ? e().removeItem(r) : delete t[r];
    },
    clear() {
      n() ? e().clear() : (t = {});
    },
    key(r) {
      return n() ? e().key(r) : Object.keys(t)[r] || null;
    },
    get length() {
      return n() ? e().length : Object.keys(t).length;
    },
  };
}
// sessionStorage is per browser tab: same shopper can open multiple invoice URLs without
// rocketfuel_* keys last-writing across invoices (unlike localStorage for the whole origin).
const DbStorage = storageFactory(() => sessionStorage);
// Vue 3: use globalProperties (app.prototype does not exist — breaks store/router/axios that import app)
app.config.globalProperties.$DbStorage = DbStorage;
// Clear persisted tab data only on select-currency/:uuid route.
if (/^\/select-currency\/[0-9a-fA-F-]+$/.test(window.location.pathname)) {
  DbStorage.clear();
}

fetchAndLogVersionJson();
initializeBrowserOtel();

// sentry integration
Sentry.init({
  app,
  dsn: config.sentry.dns,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: config.sentry.tracePropagationTargets,
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// initialize plugins
app.use(VueQueryPlugin, {
  queryClient,
  enableDevtoolsV6Plugin: true,
});
app.use(WagmiPlugin, { config: wagmiAdapter.wagmiConfig });
app.use(BootstrapVue);
const messages = Object.assign(languages);
app.use(ListGroupPlugin);
app.use(TooltipPlugin);
app.use(BadgePlugin);
app.use(ModalPlugin);
app.use(AlertPlugin);
app.use(SpinnerPlugin);
app.use(VueReCaptcha, {
  siteKey:
    process.env.VUE_APP_RECAPTCHA_KEY ||
    "6LfNmiEaAAAAAAU2ex5mKRZSR8EZWnKnDJW1co8s",
});
app.use(vuescroll);
// initialize coponents
app.component("Form", Form);
app.component("back-button-custom", BackCustomBtn);
app.component("Field", Field);
app.component("ErrorMessage", ErrorMessage);
app.component("default-layout", DefaultLayout);
app.component("loader", Loader);
app.component("magiclogin", SavePaymentMethodModal);
app.component("powered-by", PoweredBy);
app.component("back-btn", BackBtn);
app.component("crypto-wallet", CryptoWalletVue);
app.component("wallet-connect", WalletConnectVue);
app.component("box-loader", BoxLoaderVue);
app.component("partial-payments", partialPaymentsVue);
app.use(VueMask);
app.use(VueQr);
app.component("resize-sensor", resizeSensor);
app.use(VueClipboard);
DecimalNumber.set({ precision: 50 });
app.component("sardine", Sardine);
app.component("text-input", TextInput);
app.component("vertical-progress-bar", VerticalProgressBarVue);
app.component("b-collapse", CustomCollapse);
app.use(store);
window.EventBus = emitter;
app.config.productionTip = false;

const i18n = createI18n({
  locale: defaultLocale,
  messages,
});
app.use(i18n);

function currencyIconUrl(itemName) {
  const icon_name = itemName.toLowerCase().replace(" ", "_");
  return `https://rocketfuel-assets.s3.amazonaws.com/assets/${config.env.asset}/coins/${icon_name}.svg`;
}

app.mixin({
  mounted() {
    window.EventBus.on("bv::collapse::state", (collapseId, isJustShown) => {});
  },
  data() {
    return {
      coinbaseClientId: process.env.VUE_APP_COINBASE_CLIENT_ID,
      send_limit_coinbase: process.env.VUE_APP_COINBASE_SEND_LIMIT || 1.0,
    };
  },
  computed: {
    ...mapGetters({
      redirectUrl: "user/getRedirectUrl",
      enableGlobalIframeRedirect: "user/getGlobalIframeRedirect",
      enableMerchantIframeRedirect: "user/getMerchantIframeRedirect",
      cart: "user/cart",
      initialCart: "user/initialCart",
      store: "user/marketInfo",
    }),
    brandName() {
      return config.brand.name;
    },
    pageTitle() {
      return config.brand.pageTitle;
    },
  },
  methods: {
    decodeHTMLEntities(text) {
      // Create a new element or use one from cache, to save some element creation overhead
      const el = document.createElement("div");

      const enc = text
        // Prevent any mixup of existing pattern in text
        .replace(/⪪/g, "⪪#")
        // Encode entities in special format. This will prevent native element encoder to replace any amp characters
        .replace(/&([a-z1-8]{2,31}|#x[0-9a-f]+|#\d+);/gi, "⪪$1⪫");

      // Encode any HTML tags in the text to prevent script injection
      el.textContent = enc;

      // Decode entities from special format, back to their original HTML entities format
      el.innerHTML = el.innerHTML
        .replace(/⪪([a-z1-8]{2,31}|#x[0-9a-f]+|#\d+)⪫/gi, "&$1;")
        .replace(/⪪#/g, "⪪");

      // Get the decoded HTML entities
      const dec = el.textContent;

      // Clear the element content, in order to preserve a bit of memory (it is just the text may be pretty big)
      el.textContent = "";

      return dec;
    },
    notifyPaymentResultToParent(data) {
      return sendPaymentResult(data, store.getters["user/marketInfo"]);
    },
    releaseHeldPaymentResult() {
      return releasePaymentResult();
    },
    notifyParentWindow(data) {
      if (data.type === "rocketfuel_iframe_close") {
        data.redirectUrl = this.redirectUrl || "";
        const cartSource =
          this.initialCart?.order || this.initialCart?.uuid
            ? this.initialCart
            : this.cart;
        if (cartSource?.order || cartSource?.uuid) {
          store.dispatch(`user/${IFRAME_STATUS}`, {
            offerId: cartSource.order,
            hostedPageId: cartSource.uuid,
            merchantId: cartSource.merchant_id,
            status: IFRAME_STATUS_CLOSE,
          });
        }
      }
      if (window.parent) {
        window.parent.postMessage(data, "*");
      }
      if (window.top && window.top !== window.parent) {
        window.top.postMessage(data, "*");
      }
    },
    setDisplayCurrency() {
      /*
        - Commenting this in order to avoid Bug
        - https://rocketfuelblockchain1.atlassian.net/browse/ROC-2919
        - You Pay get's hidden due to this issue
      */
      const currencies = store.getters["user/currencies"];
      const settings = store.getters["user/settings"];
      if (currencies && currencies.length && settings.displayCurrency) {
        const currency = currencies.find(
          (val) => val.id === settings.displayCurrency.toUpperCase()
        );
        // display currency needs to be an object the currency
        store.commit("user/setDisplayCurrency", currency);
      }
      // if (
      //   Object.values(currencies).length &&
      //   Object.values(settings).length
      // ) {
      //     currencies.forEach(currency => {
      //     if (helper.getLowerCase(currency.id)
      //         === helper.getLowerCase(settings.displayCurrency)) {
      // store.commit("user/setDisplayCurrency", settings.displayCurrency);
      //       }
      //     });
      // }
    },
    goToResetPage(data, amount) {
      this.$router.push({
        name: "Coinbase reset",
        props: {
          amount,
          data,
        },
        query: {
          amount,
          data: JSON.stringify({ name: data.name, stockId: data.stockId }),
        },
      });
    },
    DecimalNumber(value) {
      return new DecimalNumber(value || 0);
    },
    NumberFormat(value, fixed) {
      return new Intl.NumberFormat("EN-en", {
        maximumFractionDigits: fixed || 8,
      }).format(value);
    },
    Encrypt(data) {
      const secret = JSON.stringify(data);
      const passphrase =
        process.env.VUE_APP_SECRET_KEY || "dfv9345rjMCKHSMbIrbTs23ef";
      const encrypted = CryptoJS.AES.encrypt(secret, passphrase).toString();
      return encrypted;
    },
    Decrypt(encrypted) {
      const passphrase =
        process.env.VUE_APP_SECRET_KEY || "dfv9345rjMCKHSMbIrbTs23ef";
      const bytes = CryptoJS.AES.decrypt(encrypted, passphrase);
      if (bytes.sigBytes === 0) {
        return null; // Decryption failed
      }
      const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      return decryptedData;
    },
    randomString(i) {
      let rnd = "";
      while (rnd.length < i) {
        rnd += Math.random().toString(36).substring(2);
      }
      return rnd.substring(0, i);
    },
    randomEncrypt(data) {
      const textData = JSON.stringify(data);
      const passphrase = this.randomString(32); // dfv9345rjMCKHSMbIrbTs23ef
      const encrypted = CryptoJS.AES.encrypt(textData, passphrase).toString();
      return {
        key: passphrase,
        encrypted,
      };
    },
    encryptStringWithRSA(toEncrypt) {
      const buffer = Buffer.from(toEncrypt);
      const encrypted = crypto.publicEncrypt(
        process.env.VUE_APP_PUBLIC_RSA_KEY,
        buffer
      );
      return encrypted.toString("base64");
    },
    getPaymentOptions(allStockInfo, settings, paymentOptions, cartInfo) {
      const allStocks = this.$store.getters["user/allStocks"];
      let stocks = {
        default:
          settings.method && settings.isUserConfigured
            ? helper.isBankPayment(settings.method)
              ? helper.getLowerCase(BANK)
              : settings.method
            : helper.getLowerCase(WALLET_TRANSFER),
        legacyStockFormat: [],
      };

      const isCryptoEnable = helper.isCryptoEnabled(paymentOptions);
      const isBankPaymentEnabled = helper.enableBankPayment(
        settings,
        paymentOptions,
        cartInfo
      );
      const isWalletEnabled = helper.isWalletEnabled(paymentOptions);

      // wallet
      stocks[helper.getLowerCase(WALLET_TRANSFER)] = {
        tabTitle: this.$t("tabs.wallet"),
        type: helper.getLowerCase(WALLET_TRANSFER),
        value: helper.getLowerCase(WALLET_TRANSFER),
        connected: false,
        isEnabled: isWalletEnabled,
        defaultOption: [],
        dropdownOptions: [],
      };
      // crypto exchange
      stocks[helper.getLowerCase(CRYPTO)] = {
        tabTitle: this.$t("tabs.crypto"),
        type: helper.getLowerCase(CRYPTO),
        value: helper.getLowerCase(CRYPTO),
        connected: false,
        isEnabled: isCryptoEnable,
        defaultOption: [],
        dropdownOptions: [],
      };
      // Bank
      stocks[helper.getLowerCase(BANK)] = {
        tabTitle: this.$t("tabs.bank"),
        type: helper.getLowerCase(BANK_TRANSFER),
        paymentMethod: helper.getLowerCase(BANK_TRANSFER),
        value: helper.getLowerCase(BANK),
        connected: false,
        isEnabled: isBankPaymentEnabled,
        defaultOption: [],
        dropdownOptions: [],
      };
      stocks.default = this.getDefaultTabName(
        stocks,
        isBankPaymentEnabled,
        isCryptoEnable,
        isWalletEnabled
      );
      /*
      Logic to add Connected accounts
      */
      Object.keys(allStockInfo).forEach((key) => {
        // if the payment option is disabled no need to append
        if (
          (helper.isCryptoPayment(allStockInfo[key].paymentMethod) &&
            !isCryptoEnable) ||
          (helper.isBankToBankTransfer(allStockInfo[key].paymentMethod) &&
            !isBankPaymentEnabled)
        ) {
          return;
        }
        const icon = helper.getStockLogo(key);
        // this is the default Setting
        let keyName = allStockInfo[key].paymentMethod;
        if (BANKS_ARR.includes(helper.getLowerCase(keyName))) {
          keyName = helper.getLowerCase(BANK);
        }
        const dataToAppend = {
          name: allStockInfo[key].stock.logo,
          icon,
          value: key,
          connected: true,
          accounts: allStockInfo[key] ? allStockInfo[key] : [],
          paymentMethod: allStockInfo[key].paymentMethod,
        };
        if (stocks[key]?.accounts[0]?.id === settings?.accountId) {
          // default setting
          // to do is user config false
          if (settings?.method !== helper.getLowerCase(BANK_TRANSFER)) {
            stocks.default = keyName;
          }
          stocks.legacyStockFormat.unshift(dataToAppend);
          stocks[keyName]?.dropdownOptions.unshift(dataToAppend);
          stocks[keyName].connected = true;
        } else {
          stocks.legacyStockFormat.push(dataToAppend);
          stocks[keyName]?.dropdownOptions.push(dataToAppend);
          stocks[keyName].connected = true;
        }
      });
      stocks = this.getNonConnectedWallets(allStocks, stocks, paymentOptions);
      stocks = this.appendWalletOption(isWalletEnabled, stocks);
      // stocks = this.appendBankOption(isBankPaymentEnabled, stocks);
      // remove dwolla from exchanges since it is a bank payment
      stocks.legacyStockFormat = helper.removeDwollaFromStock(
        stocks.legacyStockFormat
      );
      stocks[helper.getLowerCase(BANK)].dropdownOptions =
        helper.removeDwollaFromStock(
          stocks[helper.getLowerCase(BANK)].dropdownOptions
        );

      stocks[helper.getLowerCase(CRYPTO)].dropdownOptions =
        helper.removeDwollaFromStock(
          stocks[helper.getLowerCase(CRYPTO)].dropdownOptions
        );

      stocks[helper.getLowerCase(BANK)].dropdownOptions =
        helper.removeSilaFromStock(
          stocks[helper.getLowerCase(BANK)].dropdownOptions
        );
      if (
        !stocks[helper.getLowerCase(CRYPTO)].dropdownOptions.find(
          (val) => val.connected
        )
      ) {
        stocks[helper.getLowerCase(CRYPTO)].dropdownOptions.sort((a, b) =>
          a.value > b.value ? 1 : b.value > a.value ? -1 : 0
        );
      }

      return stocks;
    },
    getExchangeOptions() {
      alert("get exchange options");
    },
    getNonConnectedWallets(allStocks, stocks, paymentOptions) {
      // Append all enabled but not connected exchanges
      allStocks
        .filter(
          (item) =>
            paymentOptions &&
            paymentOptions.exchanges.indexOf(item.value) !== -1
        )
        .forEach((item) => {
          if (
            !stocks.legacyStockFormat.find(
              (stock) => stock.value === item.value
            )
          ) {
            item.connected = false;
            stocks.legacyStockFormat.push(item);
            stocks[helper.getLowerCase(CRYPTO)].dropdownOptions.push(item);
          }
        });
      return stocks;
    },
    appendWalletOption(isWalletEnabled, stocks) {
      // Crypto wallet & bank as an option
      const walletKey = {
        name: "Crypto wallet",
        icon: require("@/assets/img/wallets/crypto-wallet.svg"),
        value: "crypto-wallet",
        connected: true,
        paymentMethod: WALLET_TRANSFER,
      };

      if (isWalletEnabled) {
        stocks.legacyStockFormat.push(walletKey);
        stocks[helper.getLowerCase(WALLET_TRANSFER)].dropdownOptions.push(
          walletKey
        );
      }
      return stocks;
    },
    appendBankOption(isBankPaymentEnabled, stocks) {
      const bankTransfer = this.getBankTabMock();
      if (isBankPaymentEnabled) {
        stocks.legacyStockFormat.push(bankTransfer);
        stocks[helper.getLowerCase(BANK)].dropdownOptions.push(bankTransfer);
      }

      return stocks;
    },
    getBankTabMock() {
      return {
        name: "Bank",
        value: "bank",
        connected: false,
        icon: require("@/assets/img/wallets/bank.svg"),
        connect: "dwolla",
        paymentMethod: BANK_TRANSFER,
      };
    },
    getCurrencyIcon(itemName) {
      return currencyIconUrl(itemName);
    },
    getCoinsOptions(stockInfo, stockName, settings) {
      // This function is helpful for crypto and bank payment
      const coinsInfo = [];
      let icon = "";
      let temp;
      if (!stockInfo[stockName]) {
        return coinsInfo;
      }
      stockInfo[stockName].accounts.forEach((item) => {
        try {
          icon = currencyIconUrl(item.currency);
        } catch {
          icon = "";
        }
        temp = item;
        temp.icon = icon;
        // TODO Sorting on basis of Balance

        if (item.id === settings?.accountId) {
          coinsInfo.unshift(temp);
        } else {
          coinsInfo.push(temp);
        }
      });
      return coinsInfo;
    },
    getAllCoins: (currencies, paymentOptions) => {
      // All coins to pay with any coin
      const coins = [];
      let icon;
      if (currencies.length) {
        currencies.forEach((item) => {
          try {
            icon = currencyIconUrl(item.currency);
          } catch {
            // eslint-disable-next-line
            icon = "";
          }
          if (item.isPayment) {
            if (
              paymentOptions &&
              paymentOptions.wallets.find((wallet) => item.id === wallet)
            ) {
              coins.push({
                currency: item.id,
                name: item.fullTitle,
                icon,
                chainNetwork: item["chains.network"],
                chainsNetworkLogo: item["chains.networkLogo"],
                networkMsg: item.networkMsg,
                chainsNetworkFullTitle: item["chains.networkFullTitle"],
              });
            }
          }
        });
      }
      return coins;
    },
    getInLocalCurrency(item, displayCurrency) {
      if (item.balance === undefined) {
        return 0;
      }
      if (
        displayCurrency === undefined ||
        displayCurrency.currentRate === undefined
      ) {
        displayCurrency = {
          decimals: 2,
          currentRate: 1,
        };
      }
      return this.NumberFormat(
        DecimalNumber(item.balance)
          .mul(DecimalNumber(item.currentRate))
          .div(DecimalNumber(displayCurrency.currentRate)),
        displayCurrency.decimals
      );
    },
    getDisplayCurrency(userCurrency, defaultCurrency) {
      return userCurrency !== undefined ? userCurrency : defaultCurrency;
    },
    setValue(value, type) {
      if (value.connect) {
        app.config.globalProperties.$DbStorage.setItem(
          "rocketfuel_connect",
          value.connect
        );
        if (this.$store.getters["auth/isAuth"]) {
          this.$router.push({ name: "Connections" });
        } else {
          this.$router.push({ name: "SignUp" });
        }
      } else {
        this.$emit("wallet-selected", { type, value });
        window.EventBus.emit("bv::toggle::collapse", `collapse-${this.id}`);
      }
    },
    triggerCryptoCoinChange(coinInfo) {
      window.EventBus.emit("default-menu-change", coinInfo);
    },
    getDefaultTabName(
      stock,
      isBankPaymentEnabled,
      isCryptoEnable,
      isWalletEnabled
    ) {
      let defaultOption = stock.default;
      switch (stock.default) {
        case helper.getLowerCase(CRYPTO):
          if (!isCryptoEnable) {
            if (isWalletEnabled) {
              defaultOption = helper.getLowerCase(WALLET_TRANSFER);
            } else if (isBankPaymentEnabled) {
              defaultOption = helper.getLowerCase(BANK);
            }
          }
          break;
        case helper.getLowerCase(WALLET_TRANSFER):
          if (!isWalletEnabled) {
            if (isCryptoEnable) {
              defaultOption = helper.getLowerCase(CRYPTO);
            } else if (isBankPaymentEnabled) {
              defaultOption = helper.getLowerCase(BANK);
            }
          }
          break;
        case helper.getLowerCase(BANK):
          if (!isBankPaymentEnabled) {
            if (isCryptoEnable) {
              defaultOption = helper.getLowerCase(CRYPTO);
            } else if (isWalletEnabled) {
              defaultOption = helper.getLowerCase(WALLET_TRANSFER);
            }
          }
          break;
      }
      return defaultOption;
    },

    // go back option
    changePartnerLabelName(marketInfo, title, plainText = false) {
      let fTitle = title;
      if (!plainText) {
        fTitle = this.$t(title);
      }
      if (
        marketInfo &&
        marketInfo.whiteLabel &&
        marketInfo.whiteLabel.colorSchema &&
        marketInfo.whiteLabel.colorSchema.partnerLabel
      ) {
        const tempPartnerLabel =
          this.marketInfo.whiteLabel.colorSchema.partnerLabel;
        return fTitle
          .replace(new RegExp("Rocketfuel Blockchain Inc.", "gi"), tempPartnerLabel)
          .replace(new RegExp("RocketFuel Blockchain Inc.", "gi"), tempPartnerLabel)
          .replace(new RegExp("Rocketfuel Blockchain", "gi"), tempPartnerLabel)
          .replace(new RegExp("Rocketfuel", "gi"), tempPartnerLabel)
          .replace(new RegExp("RocketFuel", "gi"), tempPartnerLabel)
          .replace(new RegExp(config.brand.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), tempPartnerLabel)
          .replace(new RegExp("RPay", "g"), tempPartnerLabel)
          .replace(new RegExp("RKFL", "gi"), tempPartnerLabel);
      }
      return fTitle;
    },
    invalidOrder() {
      router.push({ name: "404" });
    },
    openWindowPopup(popupURL, popupName = "") {
      const popupWidth = 600;
      const popupHeight = 400;
      const left = (window.innerWidth - popupWidth) / 2;
      const top = (window.innerHeight - popupHeight) / 2;
      const popupFeatures = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes`;
      // Open the popup window
      const popupWindow = window.open(popupURL, popupName, popupFeatures);
      // Check if the popup was blocked by a popup blocker
      if (
        !popupWindow ||
        popupWindow.closed ||
        typeof popupWindow.closed == "undefined"
      ) {
        alert("Popup blocked. Please allow popups for this website.");
      }
    },
    getWrappedText(text) {
      if (text && text.length > 16) {
        const length = text.length / 2;
        const truncated = text.substring(0, length);
        const last5Chars = text.substring(text.length - 10);
        return `${truncated}...${last5Chars}`;
      }
      return text;
    },
  },
});

// Router once here — store + i18n already registered above (avoid duplicate app.use)
app.use(router);
app.mount("#app");
export default app;

axios.interceptors.request.use(
  (config) => {
    if (config.url.includes(ALCHEMY_DOMAIN)) {
      return config;
    }
    // Only set authorization header if not already provided
    if (!config.headers.authorization) {
      if (store.getters["auth/isAuth"] || !Helper.checkAuthToken(config.url)) {
        if (config.url.split("/").pop() === "refresh-token") {
          config.headers.authorization = `Bearer ${store.getters["auth/refreshToken"]}`;
        } else if (Helper.checkAuthToken(config.url)) {
          config.headers.authorization = `Bearer ${store.getters["auth/accessToken"]}`;
        }
      } else if (
        config.url.split("/").pop() === "validate-email" ||
        config.url.split("/").pop() === "resend"
      ) {
        config.headers.authorization = `Bearer ${store.getters["auth/tempAccessToken"]}`;
      }
    }
    let language = "en";
    if (app.config.globalProperties.$DbStorage.getItem("lang")) {
      language =
        app.config.globalProperties.$DbStorage.getItem("lang") === "FRE"
          ? "fr"
          : app.config.globalProperties.$DbStorage.getItem("lang") === "ESP"
          ? "es"
          : "en";
    }
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  async (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // 403 forbidden (Merchant account might be used)
    if (
      error.response?.status === 403 &&
      (originalRequest?.url || "").indexOf("auth/restore/request") < 0
    ) {
      store.commit("auth/logOut");
      // Add a check to avoid Duplicate navigation error
      if (
        router.currentRoute.value.name !== "Sign in" &&
        router.currentRoute.value.name !== "Totp"
      ) {
        router.push({ name: "Sign in" });
      }
      return Promise.reject(error);
    }
    if (store.getters["auth/refreshToken"]) {
      if (
        error.response?.status === 401 &&
        originalRequest.url.split("/").pop() === "refresh-token"
      ) {
        if (router.currentRoute.value.name === routeEnum.SIGN_IN) {
          // router.push({ name: "Sign in" });
          return Promise.reject(error);
        }

        const currentUserData = store.getters["user/userInfo"];

        const currentUserSettings = store.getters["user/settings"];

        // store.commit("auth/logOut"); Avoid calling logout in order to retain user

        if (!currentUserData.email) {
          // if there is no email, no way to auto generate token or use Magic link otp flow

          store.commit("auth/logOut");
          if (router.currentRoute.value.name !== routeEnum.SIGN_IN) {
            router.push({ name: routeEnum.SIGN_IN });
          }
        }
        const sessionNotif = store.getters["user/sessionNotifyStatus"];

        // Check for new user. Only auto generate token if user is sso user and has no exchange connected
        const notifyType =
          currentUserData.ssoStatus &&
          !currentUserSettings.stockId &&
          !sessionNotif?.isBankLinked
            ? sessionTimeoutNotificationType.NEW_USER
            : sessionTimeoutNotificationType.OTP;

        if (
          !sessionNotif?.active &&
          Object.keys(currentUserData || {}).length
        ) {
          // only set when not active
          store.commit("user/setSessionNotifyStatus", {
            active: true,
            type: notifyType,
            data: { email: currentUserData.email },
            fallbackRoute: router.currentRoute.value.name,
          });
        }

        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        await store.dispatch(`auth/${REFRESH_TOKEN}`);
        return axios(originalRequest);
      }
    }
    if (!originalRequest._retry) {
      window.EventBus.emit("SERVER_ERROR", error.response);
    }
    return Promise.reject(error);
  }
);

function resetIframeSessionUiState() {
  resetPaymentResultEvents();
  store.commit("user/setIsIframeOpenEmitted", false);
  store.commit("user/setIframeDismissConfirmVisible", false);
  teardownBootstrapModals("modal-2");
  window.EventBus.emit(RESET_IFRAME_CLOSE_MODAL);
  setTimeout(() => {
    store.commit("user/setIframeDismissConfirmVisible", false);
    teardownBootstrapModals("modal-2");
    window.EventBus.emit(RESET_IFRAME_CLOSE_MODAL);
  }, 0);
}

function recordCartIntegrationActivity(cart) {
  const integration = cart?.integration || {};
  logActivityIntegrationStart({
    ...cart,
    hostedPageId: cart.uuid || cart.hostedPageId,
    merchantId: resolveCartMerchantId(cart),
    integrationType: integration.integrationType || cart.integrationType,
    integrationVersion:
      integration.integrationVersion || cart.integrationVersion,
    parentDomain: integration.parentDomain || cart.parentDomain,
  });
}

async function fetchOrderDetailsForCart(cart) {
  if (!cart?.uuid) {
    return null;
  }
  store.dispatch(`user/${SET_HOSTED_PAGE_INFO}`, cart.uuid);
  try {
    const integration = cart.integration || {};
    return await store.dispatch("auth/getOrderDetails", {
      id: cart.uuid,
      integrationType: integration.integrationType || cart.integrationType,
      integrationVersion:
        integration.integrationVersion || cart.integrationVersion,
      parentDomain: integration.parentDomain || cart.parentDomain,
    });
  } catch (err) {
    console.warn("[handleRocketfuelSendCart] getOrderDetails failed:", err);
    return null;
  }
}

async function mergeOrderDetailsIntoCart(cartData, orderDetails) {
  if (!orderDetails?.hostedPageInfo) {
    return cartData;
  }
  persistCartEmail(store, orderDetails.hostedPageInfo);
  const merged = mergeIframeCartPayload(cartData, orderDetails.hostedPageInfo);
  return applyOrderKycFields(merged, orderDetails.hostedPageInfo);
}

/** hosted-page.vue already passes full order-details payload via EventBus. */
function isCartEnrichedFromOrderDetails(cart) {
  return Boolean(
    cart?.uuid &&
      (cart.amountUsd != null ||
        cart.totalAmount != null ||
        cart.kycRequired != null)
  );
}

async function enrichCartIfNeeded(cartData) {
  if (isCartEnrichedFromOrderDetails(cartData)) {
    return cartData;
  }
  const orderDetails = await fetchOrderDetailsForCart(cartData);
  return mergeOrderDetailsIntoCart(cartData, orderDetails);
}

function commitCartToStore(cartPayload) {
  store.commit("user/setCart", cartPayload);
  store.commit("user/setInitialCart", cartPayload);
  persistCartEmail(store, cartPayload);
  store.commit("user/setRedirectUrl", cartPayload?.redirectUrl);
  store.commit(
    "user/setGlobalIframeRedirect",
    cartPayload?.enableGlobalIframeRedirect
  );
  store.commit(
    "user/setMerchantIframeRedirect",
    cartPayload?.enableMerchantIframeRedirect
  );
}

function getIncomingCartSessionKey(data) {
  return getCartSessionKey({
    merchant_id: data?.merchant_id,
    order: data?.order,
    uuid: data?.uuid,
    amount: data?.amount,
    currency: data?.currency,
  });
}

let iframeCartInitPromise = null;
let iframeCartInitKey = null;
let iframeCartCompletedKey = null;

function resetIframeCartInitState() {
  iframeCartInitPromise = null;
  iframeCartInitKey = null;
  iframeCartCompletedKey = null;
}

async function navigateAfterCartInit(routeName) {
  if (router.currentRoute.value.name === routeName) {
    return;
  }
  await router.push({ name: routeName });
}

async function ensureMerchantBranding(cart) {
  if (!cart?.merchant_id) {
    return;
  }
  await loadMerchantMarketInfo(store, cart).catch((err) => {
    console.warn(
      "[handleRocketfuelSendCart] loadMerchantMarketInfo failed:",
      err
    );
  });
}

async function runRocketfuelSendCart(data) {
  if (!isIframeCartLoadingActive()) {
    beginIframeCartLoading();
  }
  try {
    return await runRocketfuelSendCartInner(data);
  } finally {
    endIframeCartLoading();
  }
}

async function runRocketfuelSendCartInner(data) {
  applyIntegrationFromCart(data);
  resetIframeSessionUiState();

  // Same as hosted-page.vue setCartData + legacy postMessage handler: autologin reads
  // data.token / data.merchantAuth from the cart payload, not from a pre-auth order-details fetch.
  const cartData = prepareIframeCartFromPostMessage(data);

  const sendIframeOpenLog = async (cart) => {
    const isIframeOpenEmitted = store.getters["user/getIsIframeOpenEmitted"];
    if (!isIframeOpenEmitted) {
      store.commit("user/setIsIframeOpenEmitted", true);
      await store.dispatch(`user/${IFRAME_STATUS}`, {
        offerId: cart.order,
        hostedPageId: cart.uuid,
        merchantId: cart.merchant_id,
        status: IFRAME_STATUS_OPEN,
      });
    }
  };

  if (cartData.merchantAuth || cartData.isSSO) {
    let resp = null;
    store.commit("auth/logOut");

    // WooCommerce sets isSSO; use SSO only when tokens exist, else autologin.
    if (cartData.isSSO && cartData.access) {
      resp = await store.dispatch(`auth/${SSO_SIGN_IN}`, {
        access: cartData.access,
        email: cartData.email,
        status: cartData.status,
        refresh: cartData.refresh,
        skipMerchantAuth: true,
      });
    } else if (cartData.merchantAuth) {
      resp = await store.dispatch(`auth/${AUTO_SIGN_IN}`, {
        rkflToken: cartData.token,
        merchantAuth: cartData.merchantAuth,
        skipMerchantAuth: true,
      });
    }

    commitCartToStore(cartData);
    await sendIframeOpenLog(cartData);

    const enrichedCart = await enrichCartIfNeeded(cartData);
    if (enrichedCart !== cartData) {
      commitCartToStore(enrichedCart);
    }

    if (!resp?.access) {
      await ensureMerchantBranding(enrichedCart);
      await navigateAfterCartInit("Sign in");
      store.commit("user/setIsLogoutCheckUp", false);
      recordCartIntegrationActivity(data);
      return true;
    }

    await ensureMerchantBranding(enrichedCart);
    if (enrichedCart.merchant_id) {
      await prefetchPaymentBootstrap(store, enrichedCart).catch(() => {});
    }

    if (
      (!store.getters["auth/isAuth"] && !store.getters["auth/userStatus"]) ||
      store.getters["auth/ssoUser"]
    ) {
      await navigateAfterCartInit("Welcome");
    } else if (enrichedCart.userDetailChanged) {
      await navigateAfterCartInit("Payment");
    } else {
      await store.dispatch(`user/${GET_USER_INFO}`);
      await navigateAfterCartInit("Payment");
    }
    store.commit("user/setIsLogoutCheckUp", false);
    recordCartIntegrationActivity(data);
    return true;
  }

  commitCartToStore(cartData);
  store.commit("user/setIsLogoutCheckUp", false);
  await sendIframeOpenLog(cartData);
  const enrichedCart = await enrichCartIfNeeded(cartData);
  if (enrichedCart !== cartData) {
    commitCartToStore(enrichedCart);
  }
  await ensureMerchantBranding(enrichedCart);
  await navigateAfterCartInit("Sign in");
  recordCartIntegrationActivity(data);
  return true;
}

async function handleRocketfuelSendCart(data) {
  if (!data || typeof data !== "object") {
    return false;
  }

  const incomingKey = getIncomingCartSessionKey(data);

  if (
    incomingKey &&
    incomingKey === iframeCartCompletedKey &&
    store.getters["auth/isAuth"]
  ) {
    return true;
  }

  if (
    iframeCartInitPromise &&
    incomingKey &&
    incomingKey === iframeCartInitKey
  ) {
    return iframeCartInitPromise;
  }

  iframeCartInitKey = incomingKey;
  iframeCartInitPromise = runRocketfuelSendCart(data).finally(() => {
    iframeCartInitPromise = null;
  });

  const result = await iframeCartInitPromise;
  if (incomingKey && result && store.getters["auth/isAuth"]) {
    iframeCartCompletedKey = incomingKey;
  }
  return result;
}

window.EventBus.on(RESET_IFRAME_CART_INIT, resetIframeCartInitState);

window.addEventListener("message", async (event) => {
  applyIntegrationFromParentMessage(event);

  if (event.data?.type === "rocketfuel_send_cart" && event.data?.data) {
    await handleRocketfuelSendCart(event.data.data);
    return;
  }

  if (
    event.data?.type === "rocketfuel_iframe_open" ||
    event.data?.type === "rocketfuel_show"
  ) {
    resetIframeSessionUiState();
    resetIframeCartInitState();
    return;
  }

  if (event.data?.type === "rocketfuel_max_height") {
    document.getElementsByTagName(
      "body"
    )[0].style.height = `${event.data.data}px`;
    return;
  }

  if (event.data?.type === "rocketfuel_connect_result") {
    window.EventBus.emit("CONNECTION_RESULT", event.data);
  }
});

window.EventBus.on("rocketfuel_send_cart", handleRocketfuelSendCart);

// window.onbeforeunload = function () {
//   const isInIframe = window.top !== window.self;
//   if (isInIframe) return undefined;
//   return "Are you sure you want to leave this page?";
// };
